import React from 'react';
import { Table, Tag, Button } from 'antd';
import { DownloadOutlined, LoadingOutlined, CheckOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {OrderInfoDto} from "../../../../utils/types";
import {i18n} from "../../../../i18n";
import {Color} from "../../../../style/custom/colors";
import {DownloadState} from "../../../../utils/hooks/useDownloadPdf";


const MedicationOrderTable: React.FC<MedicationOrderTableProps> = ({
                                                                       data,
                                                                       loading = false,
                                                                       onDownload,
                                                                       downloadStates = {}
                                                                   }) => {
    const getDownloadButtonContent = (record: OrderInfoDto) => {
        const downloadState = downloadStates[record.pdfId] || 'idle';

        switch (downloadState) {
            case 'downloading':
                return {
                    icon: <LoadingOutlined spin />,
                    text: i18n.t('therapyTab.medicationOrderTable.downloadButton.downloading'),
                    disabled: true,
                    type: 'default' as const,
                    style: {}
                };
            case 'success':
                return {
                    icon: <CheckOutlined />,
                    text: i18n.t('therapyTab.medicationOrderTable.downloadButton.downloaded'),
                    disabled: true,
                    type: 'primary' as const,
                    style: {
                        backgroundColor: Color.green,
                        borderColor: Color.green,
                        color: Color.white
                    }
                };
            case 'error':
                return {
                    icon: <ExclamationCircleOutlined />,
                    text: i18n.t('therapyTab.medicationOrderTable.downloadButton.retry'),
                    disabled: false,
                    type: 'default' as const,
                    style: {
                        backgroundColor: Color.warningStrong,
                        borderColor: Color.warningStrong,
                        color: Color.white
                    }
                };
            default:
                return {
                    icon: <DownloadOutlined />,
                    text: i18n.t('therapyTab.medicationOrderTable.downloadButton.download'),
                    disabled: false,
                    type: 'default' as const,
                    style: {}
                };
        }
    };

    const columns: ColumnsType<OrderInfoDto> = [
        {
            title: i18n.t('therapyTab.medicationOrderTable.columns.status'),
            dataIndex: 'submissionSucceeded',
            key: 'submissionSucceeded',
            width: 150,
            render: (submissionSucceeded: boolean) => (
                <Tag
                    color={submissionSucceeded ? Color.green : Color.warningStrong}
                    style={{
                        fontWeight: 'bold',
                        padding: '2px 6px',
                        fontSize: '12px',
                        borderRadius: '10px',
                    }}
                >
                    {submissionSucceeded ? i18n.t('common.success') : i18n.t('common.fail')}
                </Tag>
            ),
        },
        {
            title: i18n.t('therapyTab.medicationOrderTable.columns.timestamp'),
            dataIndex: 'submittedTimestamp',
            key: 'submittedTimestamp',
            width: 200,
        },
        {
            title: i18n.t('therapyTab.medicationOrderTable.columns.order'),
            dataIndex: 'orderId',
            key: 'orderId',
            width: 250,
        },
        {
            title: i18n.t('therapyTab.medicationOrderTable.columns.tpPdf'),
            key: 'download',
            width: 150,
            align: 'center',
            render: (_, record: OrderInfoDto) => {
                const buttonProps = getDownloadButtonContent(record);
                return (
                    <Button
                        type={buttonProps.type}
                        size="small"
                        icon={buttonProps.icon}
                        onClick={() => !buttonProps.disabled && onDownload?.(record.pdfId, record.orderId)}
                        disabled={buttonProps.disabled}
                        style={{
                            minWidth: '110px',
                            ...buttonProps.style,
                            borderRadius: '10px',
                        }}
                    >
                        {buttonProps.text}
                    </Button>
                );
            },
        },
    ];

    return (
        <Table<OrderInfoDto>
            columns={columns}
            dataSource={data || []}
            loading={loading}
            rowKey="id"
            pagination={false}
            size="middle"
            scroll={{ x: 720 }}
        />
    );
};

export interface MedicationOrderTableProps {
    data: OrderInfoDto[] | null;
    loading?: boolean;
    onDownload?: (pdfId: string, orderId: string) => void;
    downloadStates?: Record<string, DownloadState>;
}

export default MedicationOrderTable;